import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";

// Modelos de mensagem para campanha por CHIP (conta não oficial). Sem aprovação
// da Meta: texto com {{1}}, {{2}}... e variações {Olá|Oi}, mais anexo opcional.

const temVariacao = texto => /\{[^{}]*\|[^{}]*\}/.test(texto || "");
const temSaida = texto => /\b(sair|parar|stop|descadastr)/i.test(texto || "");

// Avisos (não bloqueiam): o que mais pesa para o WhatsApp bloquear um chip.
const avisosDoTexto = texto => {
  const avisos = [];
  if (texto && !temVariacao(texto)) {
    avisos.push(
      "Sem variação de texto: todos recebem a mensagem idêntica, o que o WhatsApp trata como robô. Use, por exemplo, {Olá|Oi|Bom dia}."
    );
  }
  if (texto && !temSaida(texto)) {
    avisos.push('Sem forma de sair da lista. Sugestão: "Responda SAIR para não receber mais."');
  }
  return avisos;
};

const vazio = { id: null, name: "", body: "", mediaName: null, removeMedia: false };

const ChipTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(vazio);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chip-templates");
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrir = modelo => {
    setForm(modelo ? { ...vazio, ...modelo, removeMedia: false } : vazio);
    setFile(null);
    setModalOpen(true);
  };

  const salvar = async () => {
    if (!form.name.trim()) return toast.warning("Dê um nome ao modelo.");
    if (!form.body.trim() && !file && !(form.mediaName && !form.removeMedia)) {
      return toast.warning("Escreva o texto do modelo ou anexe um arquivo.");
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("body", form.body);
      if (file) fd.append("media", file);
      if (form.removeMedia && !file) fd.append("removeMedia", "true");
      if (form.id) await api.put(`/chip-templates/${form.id}`, fd);
      else await api.post("/chip-templates", fd);
      toast.success("Modelo salvo.");
      setModalOpen(false);
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const excluir = async modelo => {
    try {
      await api.delete(`/chip-templates/${modelo.id}`);
      toast.success("Modelo removido.");
      carregar();
    } catch (err) {
      toastError(err);
    }
    setDeleting(null);
  };

  const anexoAtual = file ? file.name : form.mediaName && !form.removeMedia ? form.mediaName : null;
  const avisos = avisosDoTexto(form.body);

  return (
    <MainContainer>
      <ConfirmationModal
        title={deleting ? `Excluir o modelo "${deleting.name}"?` : "Excluir modelo"}
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => excluir(deleting)}
      >
        Campanhas já criadas com ele não são afetadas.
      </ConfirmationModal>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? "Editar modelo não oficial" : "Novo modelo não oficial"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome do modelo"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            label="Texto"
            fullWidth
            multiline
            minRows={5}
            margin="dense"
            value={form.body}
            onChange={e => setForm({ ...form, body: e.target.value })}
            helperText="Use {{1}}, {{2}}... para os dados da planilha (o {{1}} é o nome quando a lista vem de Contatos) e {Olá|Oi|Bom dia} para variar o texto entre os contatos."
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Button size="small" startIcon={<UploadFileOutlined />} onClick={() => fileRef.current && fileRef.current.click()}>
              {anexoAtual ? "Trocar anexo" : "Anexar imagem ou PDF"}
            </Button>
            {anexoAtual && (
              <Chip
                size="small"
                label={anexoAtual}
                onDelete={() => {
                  setFile(null);
                  setForm(f => ({ ...f, removeMedia: true }));
                }}
              />
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/mp4,application/pdf"
              hidden
              onChange={e => {
                const f = e.target.files && e.target.files[0];
                if (fileRef.current) fileRef.current.value = "";
                if (f) setFile(f);
              }}
            />
          </Box>
          {avisos.map((a, i) => (
            <Alert key={i} severity="warning" sx={{ mt: 1, fontSize: 12 }}>
              {a}
            </Alert>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvar} disabled={saving}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <MainHeader>
        <Title>
          Modelos não oficiais (chip) {templates.length > 0 ? `(${templates.length})` : ""}
        </Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<AddCircleOutline />} onClick={() => abrir(null)}>
            Novo modelo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Typography variant="body2" color="textSecondary" sx={{ px: 1, pb: 1 }}>
        Usados nas campanhas por conta não oficial. Não passam por aprovação da Meta — revise o texto com cuidado.
      </Typography>

      <Paper variant="outlined" sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Texto</TableCell>
              <TableCell align="center">Anexo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map(m => (
              <TableRow key={m.id}>
                <TableCell sx={{ fontWeight: 600 }}>{m.name}</TableCell>
                <TableCell sx={{ whiteSpace: "pre-wrap", maxWidth: 520, fontSize: 13 }}>
                  {(m.body || "").length > 220 ? `${m.body.slice(0, 220)}…` : m.body}
                  {avisosDoTexto(m.body).length > 0 && (
                    <Tooltip title={avisosDoTexto(m.body).join(" ")} arrow>
                      <Chip size="small" color="warning" variant="outlined" label="revisar" sx={{ ml: 1 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 12 }}>
                  {m.mediaName || "—"}
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => abrir(m)}>
                    <Edit />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(m)}>
                    <DeleteOutline />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Nenhum modelo ainda. Crie o primeiro em "Novo modelo".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default ChipTemplates;
